import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Palette, Plus, Save, Trash2, Star, StarOff, Copy, Eye, FileText, Receipt, ChevronDown, ChevronRight, Upload as UploadIcon, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import LogoDragCanvas from "@/components/LogoDragCanvas";
import HeaderDragCanvas from "@/components/HeaderDragCanvas";

// Debounce helper
function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

const FONT_OPTIONS = [
  { label: "System sans-serif", value: "'Helvetica Neue', Arial, sans-serif" },
  { label: "Georgia (serif)", value: "Georgia, 'Times New Roman', serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Impact (display)", value: "Impact, 'Arial Black', sans-serif" },
  { label: "Courier (mono)", value: "'Courier New', Courier, monospace" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
];

export default function DocumentDesigner() {
  const [templates, setTemplates] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [design, setDesign] = useState(null);
  const [meta, setMeta] = useState({ name: "", preset: "classic", is_default_estimate: false, is_default_invoice: false });
  const [previewKind, setPreviewKind] = useState("estimate");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", preset: "classic" });
  const [businessLogoUrl, setBusinessLogoUrl] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const logoFileInputRef = useRef(null);
  const iframeRef = useRef(null);

  const load = useCallback(async () => {
    // Templates + presets are REQUIRED for the designer to render.
    // Business info is optional enrichment (logo/name defaults) — fetched
    // separately so a /business failure never blocks the whole page and,
    // more importantly, so any 404 or 401 there is reported with the exact
    // endpoint name instead of being hidden by a swallowing .catch().
    try {
      const [t, p] = await Promise.all([
        api.get("/document-templates"),
        api.get("/document-templates/presets"),
      ]);
      setTemplates(t.data);
      setPresets(p.data.presets || []);
      if (t.data.length && !selectedId) {
        selectTemplate(t.data[0]);
      }
    } catch (e) {
      const url = e?.config?.url || "unknown endpoint";
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      console.error("[DocumentDesigner] template load failed", { url, status, detail });
      toast.error(`Failed to load templates from ${url}${status ? ` (${status})` : ""}`);
      return; // don't attempt /business if the required data failed
    }

    // Optional: business logo + display name. If this fails we surface a
    // targeted, actionable toast rather than silently zeroing it out.
    try {
      const biz = await api.get("/business");
      setBusinessLogoUrl(biz.data?.logo_url || null);
      setBusinessName(biz.data?.name || biz.data?.business_name || "");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      console.warn("[DocumentDesigner] /business load failed", { status, detail });
      // Sensible defaults so the designer keeps working
      setBusinessLogoUrl(null);
      setBusinessName("");
      if (status === 404) {
        toast.warning("Business profile not found — visit Settings to create one. Templates still work.");
      } else if (status && status !== 401) {
        toast.warning(`Couldn't load business branding (${status}). Templates still work.`);
      }
    }
  }, [selectedId]);

  useEffect(() => { load(); /* initial */ }, []); // eslint-disable-line

  const selectTemplate = (t) => {
    setSelectedId(t.id);
    setDesign(t.design);
    setMeta({
      name: t.name,
      preset: t.preset,
      is_default_estimate: t.is_default_estimate,
      is_default_invoice: t.is_default_invoice,
    });
    setDirty(false);
  };

  const createTemplate = async () => {
    try {
      const { data } = await api.post("/document-templates", newForm);
      toast.success("Template created");
      setNewOpen(false);
      setNewForm({ name: "", preset: "classic" });
      const list = await api.get("/document-templates");
      setTemplates(list.data);
      selectTemplate(data);
    } catch { toast.error("Create failed"); }
  };

  const duplicateTemplate = async () => {
    if (!design) return;
    try {
      const { data } = await api.post("/document-templates", {
        name: `${meta.name} (copy)`,
        preset: meta.preset,
        design,
      });
      const list = await api.get("/document-templates");
      setTemplates(list.data);
      selectTemplate(data);
      toast.success("Duplicated");
    } catch { toast.error("Duplicate failed"); }
  };

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.put(`/document-templates/${selectedId}`, { name: meta.name, preset: meta.preset, design });
      setDirty(false);
      toast.success("Saved");
      const list = await api.get("/document-templates");
      setTemplates(list.data);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!selectedId) return;
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/document-templates/${selectedId}`);
      toast.success("Deleted");
      setSelectedId(null);
      setDesign(null);
      const list = await api.get("/document-templates");
      setTemplates(list.data);
      if (list.data.length) selectTemplate(list.data[0]);
    } catch { toast.error("Delete failed"); }
  };

  const setAsDefault = async (kind) => {
    if (!selectedId) return;
    try {
      await api.post(`/document-templates/${selectedId}/set-default`, { kind });
      const list = await api.get("/document-templates");
      setTemplates(list.data);
      const cur = list.data.find(x => x.id === selectedId);
      if (cur) setMeta({ ...meta, is_default_estimate: cur.is_default_estimate, is_default_invoice: cur.is_default_invoice });
      toast.success(`Set as default ${kind} template`);
    } catch { toast.error("Failed"); }
  };

  const applyPreset = (key) => {
    const p = presets.find(x => x.key === key);
    if (!p) return;
    setDesign(p.design);
    setMeta({ ...meta, preset: key });
    setDirty(true);
  };

  // ---- Design state updater helpers ----
  const patch = (path, value) => {
    setDesign(prev => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      let ref = next;
      const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i++) {
        ref[parts[i]] = ref[parts[i]] || {};
        ref = ref[parts[i]];
      }
      ref[parts[parts.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  // Batch several `dotted.path` → value updates in a single render so that
  // paired fields (e.g. free_x + free_y) update atomically without racing.
  const applyPatches = (patches) => {
    setDesign(prev => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      for (const [path, value] of Object.entries(patches || {})) {
        let ref = next;
        const parts = path.split(".");
        for (let i = 0; i < parts.length - 1; i++) {
          ref[parts[i]] = ref[parts[i]] || {};
          ref = ref[parts[i]];
        }
        ref[parts[parts.length - 1]] = value;
      }
      return next;
    });
    setDirty(true);
  };

  // ---- Template-specific logo upload ----
  const pickTemplateLogo = () => logoFileInputRef.current?.click();

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Logo file must be under 5MB"); return; }
    try {
      const dataUrl = await downscaleImageToDataUrl(f, 800);
      patch("layout.template_logo_url", dataUrl);
      toast.success("Logo added to this template");
    } catch {
      toast.error("Failed to read image");
    }
  };

  const clearTemplateLogo = () => {
    if (!design?.layout?.template_logo_url) return;
    if (!window.confirm("Remove this template's custom logo? The shared business logo will be used instead.")) return;
    patch("layout.template_logo_url", null);
  };

  // Effective logo shown in the drag canvas: template override > business logo
  const effectiveLogoUrl = design?.layout?.template_logo_url || businessLogoUrl;
  const templateHasCustomLogo = !!design?.layout?.template_logo_url;

  // ---- Live preview via iframe ----
  const debouncedDesign = useDebounced(design, 300);
  const debouncedKind = useDebounced(previewKind, 100);
  useEffect(() => {
    if (!debouncedDesign || !iframeRef.current) return;
    let canceled = false;
    (async () => {
      try {
        const res = await api.post("/document-templates/preview", { kind: debouncedKind, design: debouncedDesign }, { responseType: "text", headers: { Accept: "text/html" } });
        if (canceled) return;
        const html = typeof res.data === "string" ? res.data : "";
        const doc = iframeRef.current?.contentDocument;
        if (doc) { doc.open(); doc.write(html); doc.close(); }
      } catch { /* ignore */ }
    })();
    return () => { canceled = true; };
  }, [debouncedDesign, debouncedKind]);

  if (!design) {
    return (
      <div className="p-8">
        <Header onNew={() => setNewOpen(true)} />
        {templates.length === 0 ? (
          <EmptyState presets={presets} onCreate={(preset) => { setNewForm({ name: `${preset.name} Template`, preset: preset.key }); setNewOpen(true); }} />
        ) : (
          <div className="text-center text-zinc-500 py-16">Select a template from the sidebar</div>
        )}
        <NewTemplateDialog open={newOpen} onOpenChange={setNewOpen} presets={presets} form={newForm} setForm={setNewForm} onCreate={createTemplate} />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <Header onNew={() => setNewOpen(true)} />

      <div className="grid grid-cols-12 gap-6 mt-6">
        {/* LEFT — Template list */}
        <aside className="col-span-12 md:col-span-3 lg:col-span-2">
          <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-2">Your templates</div>
          <div className="space-y-2" data-testid="template-list">
            {templates.map(t => (
              <TemplateThumbnail
                key={t.id}
                template={t}
                selected={t.id === selectedId}
                onSelect={() => selectTemplate(t)}
              />
            ))}
          </div>
        </aside>

        {/* MIDDLE — Controls */}
        <div className="col-span-12 md:col-span-9 lg:col-span-4 space-y-3" data-testid="designer-controls">
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Input value={meta.name} onChange={(e) => { setMeta({ ...meta, name: e.target.value }); setDirty(true); }} placeholder="Template name" data-testid="template-name-input" />
              <Button variant="outline" size="sm" onClick={duplicateTemplate} title="Duplicate" data-testid="template-duplicate-btn"><Copy className="w-4 h-4"/></Button>
              <Button variant="outline" size="sm" onClick={remove} title="Delete" data-testid="template-delete-btn"><Trash2 className="w-4 h-4 text-rose-500"/></Button>
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button
                variant={meta.is_default_estimate ? "default" : "outline"}
                size="sm"
                onClick={() => setAsDefault("estimate")}
                className={meta.is_default_estimate ? "bg-[#FF5A00] hover:bg-[#E04F00] text-white" : ""}
                data-testid="set-default-estimate-btn"
              >
                {meta.is_default_estimate ? <Star className="w-3.5 h-3.5 mr-1"/> : <StarOff className="w-3.5 h-3.5 mr-1"/>}
                Default for estimates
              </Button>
              <Button
                variant={meta.is_default_invoice ? "default" : "outline"}
                size="sm"
                onClick={() => setAsDefault("invoice")}
                className={meta.is_default_invoice ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                data-testid="set-default-invoice-btn"
              >
                {meta.is_default_invoice ? <Star className="w-3.5 h-3.5 mr-1"/> : <StarOff className="w-3.5 h-3.5 mr-1"/>}
                Default for invoices
              </Button>
            </div>
          </div>

          {/* Preset picker */}
          <Section title="Starting preset" defaultOpen>
            <div className="grid grid-cols-2 gap-2">
              {presets.map(p => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className={`text-left p-3 rounded border transition ${design.preset === p.key ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200 hover:border-zinc-400 bg-white"}`}
                  data-testid={`preset-${p.key}-btn`}
                >
                  <div className="font-semibold text-sm">{p.name}</div>
                  <div className="text-[11px] text-zinc-500 mt-1 leading-snug">{p.description}</div>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-zinc-400 mt-2">Picking a preset replaces all styles. Fine-tune from any of the sections below.</p>
          </Section>

          {/* Typography */}
          <Section title="Typography">
            <Field label="Font family">
              <Select value={design.typography?.font_family} onValueChange={(v) => patch("typography.font_family", v)}>
                <SelectTrigger data-testid="typography-font-family"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Body size (px)"><NumberInput value={design.typography?.base_size_px} min={8} max={20} onChange={(v) => patch("typography.base_size_px", v)} testid="typography-base-size"/></Field>
              <Field label="Heading size (px)"><NumberInput value={design.typography?.heading_size_px} min={14} max={60} onChange={(v) => patch("typography.heading_size_px", v)} testid="typography-heading-size"/></Field>
            </div>
            <Field label="Line height"><NumberInput step={0.05} value={design.typography?.line_height} min={1} max={3} onChange={(v) => patch("typography.line_height", v)} testid="typography-line-height"/></Field>
            <div className="flex items-center gap-4 mt-1">
              <Toggle label="Bold headings" checked={design.typography?.heading_bold} onChange={(v) => patch("typography.heading_bold", v)} testid="typography-heading-bold"/>
              <Toggle label="Italic headings" checked={design.typography?.heading_italic} onChange={(v) => patch("typography.heading_italic", v)} testid="typography-heading-italic"/>
            </div>
            <Field label="Heading alignment">
              <SegmentedInput value={design.typography?.heading_align} onChange={(v) => patch("typography.heading_align", v)} options={[["left","Left"],["center","Center"],["right","Right"]]} testid="typography-heading-align"/>
            </Field>
          </Section>

          {/* Colors */}
          <Section title="Colors">
            <div className="grid grid-cols-2 gap-2">
              <ColorField label="Primary" value={design.colors?.primary} onChange={(v)=>patch("colors.primary", v)} testid="color-primary"/>
              <ColorField label="Accent" value={design.colors?.accent} onChange={(v)=>patch("colors.accent", v)} testid="color-accent"/>
              <ColorField label="Text" value={design.colors?.text} onChange={(v)=>patch("colors.text", v)} testid="color-text"/>
              <ColorField label="Muted text" value={design.colors?.muted} onChange={(v)=>patch("colors.muted", v)} testid="color-muted"/>
              <ColorField label="Background" value={design.colors?.background} onChange={(v)=>patch("colors.background", v)} testid="color-background"/>
              <ColorField label="Surface" value={design.colors?.surface} onChange={(v)=>patch("colors.surface", v)} testid="color-surface"/>
              <ColorField label="Border" value={design.colors?.border} onChange={(v)=>patch("colors.border", v)} testid="color-border"/>
              <ColorField label="Totals block bg" value={design.colors?.totals_bg} onChange={(v)=>patch("colors.totals_bg", v)} testid="color-totals-bg"/>
            </div>
          </Section>

          {/* Header canvas — unified drag-and-drop for logo, business name, title */}
          <Section title="Header canvas — drag to position" defaultOpen>
            <HeaderDragCanvas
              layout={design.layout}
              content={design.content}
              effectiveLogoUrl={design.layout?.template_logo_url || businessLogoUrl}
              effectiveBusinessName={design.content?.business_name_override || businessName || "Your Business"}
              activeTitle={
                previewKind === "invoice"
                  ? (design.content?.invoice_title || "Invoice")
                  : (design.content?.estimate_title || "Estimate")
              }
              hasLogo={!!(design.layout?.template_logo_url || businessLogoUrl)}
              onChange={applyPatches}
            />
          </Section>

          {/* Header text: business name + document title */}
          <Section title="Business name and title" defaultOpen>
            <div className="text-[10px] text-zinc-500 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mb-2 leading-relaxed" data-testid="header-visual-only-note">
              <strong className="text-amber-800">Visual only.</strong> Changing what shows here won&apos;t change document
              numbering, status, payments, PDFs, or reports. An Estimate stays an Estimate; an Invoice stays an Invoice.
            </div>

            {/* Displayed business name */}
            <Field label="Displayed business name (leave blank to use your account name)">
              <Input
                value={design.content?.business_name_override ?? ""}
                onChange={(e)=>patch("content.business_name_override", e.target.value || null)}
                placeholder={businessLogoUrl ? "Uses business account name" : "Your Business"}
                data-testid="content-business-name-override"
              />
            </Field>
            <div className="flex items-center gap-4 mt-1">
              <Toggle label="Show business name" checked={design.layout?.show_business_name !== false} onChange={(v)=>patch("layout.show_business_name", v)} testid="layout-show-business-name"/>
            </div>
            {design.layout?.show_business_name !== false && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Font size (px)"><NumberInput value={design.layout?.business_name_font_size_px} min={8} max={64} onChange={(v)=>patch("layout.business_name_font_size_px", v)} testid="layout-business-name-size"/></Field>
                  <Field label="Weight">
                    <SegmentedInput value={design.layout?.business_name_weight || "semibold"} onChange={(v)=>patch("layout.business_name_weight", v)} options={[["normal","Reg"],["semibold","Semi"],["bold","Bold"]]} testid="layout-business-name-weight"/>
                  </Field>
                  <Field label="Align">
                    <SegmentedInput value={design.layout?.business_name_align || "left"} onChange={(v)=>patch("layout.business_name_align", v)} options={[["left","L"],["center","C"],["right","R"]]} testid="layout-business-name-align"/>
                  </Field>
                </div>
                <Field label="Position mode">
                  <SegmentedInput value={design.layout?.business_name_layout_mode || "anchored"} onChange={(v)=>patch("layout.business_name_layout_mode", v)} options={[["anchored","Anchored"],["free","Free"]]} testid="layout-business-name-mode"/>
                </Field>
                {design.layout?.business_name_layout_mode === "free" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="X (%)"><NumberInput step={0.1} value={design.layout?.business_name_free_x} min={0} max={100} onChange={(v)=>patch("layout.business_name_free_x", v)} testid="layout-business-name-free-x"/></Field>
                    <Field label="Y (%)"><NumberInput step={0.1} value={design.layout?.business_name_free_y} min={0} max={100} onChange={(v)=>patch("layout.business_name_free_y", v)} testid="layout-business-name-free-y"/></Field>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Nudge X (px)"><NumberInput value={design.layout?.business_name_offset_x} min={-300} max={300} onChange={(v)=>patch("layout.business_name_offset_x", v)} testid="layout-business-name-offset-x"/></Field>
                    <Field label="Nudge Y (px)"><NumberInput value={design.layout?.business_name_offset_y} min={-100} max={100} onChange={(v)=>patch("layout.business_name_offset_y", v)} testid="layout-business-name-offset-y"/></Field>
                  </div>
                )}
              </>
            )}

            <div className="h-px bg-zinc-100 my-3"/>

            {/* Document title */}
            <div className="grid grid-cols-2 gap-2">
              <Field label='Estimate title (visual "Estimate" text)'>
                <Input value={design.content?.estimate_title ?? "Estimate"} onChange={(e)=>patch("content.estimate_title", e.target.value)} data-testid="content-estimate-title"/>
              </Field>
              <Field label='Invoice title (visual "Invoice" text)'>
                <Input value={design.content?.invoice_title ?? "Invoice"} onChange={(e)=>patch("content.invoice_title", e.target.value)} data-testid="content-invoice-title"/>
              </Field>
            </div>
            <div className="flex items-center gap-4 mt-1">
              <Toggle label="Show document title" checked={design.layout?.show_title !== false} onChange={(v)=>patch("layout.show_title", v)} testid="layout-show-title"/>
            </div>
            {design.layout?.show_title !== false && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Font size (px)"><NumberInput value={design.layout?.title_font_size_px} min={12} max={80} onChange={(v)=>patch("layout.title_font_size_px", v)} testid="layout-title-size"/></Field>
                  <Field label="Weight">
                    <SegmentedInput value={design.layout?.title_weight || "bold"} onChange={(v)=>patch("layout.title_weight", v)} options={[["normal","Reg"],["semibold","Semi"],["bold","Bold"]]} testid="layout-title-weight"/>
                  </Field>
                  <Field label="Align">
                    <SegmentedInput value={design.layout?.title_align || "left"} onChange={(v)=>patch("layout.title_align", v)} options={[["left","L"],["center","C"],["right","R"]]} testid="layout-title-align"/>
                  </Field>
                </div>
                <Field label="Position mode">
                  <SegmentedInput value={design.layout?.title_layout_mode || "anchored"} onChange={(v)=>patch("layout.title_layout_mode", v)} options={[["anchored","Anchored"],["free","Free"]]} testid="layout-title-mode"/>
                </Field>
                {design.layout?.title_layout_mode === "free" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="X (%)"><NumberInput step={0.1} value={design.layout?.title_free_x} min={0} max={100} onChange={(v)=>patch("layout.title_free_x", v)} testid="layout-title-free-x"/></Field>
                    <Field label="Y (%)"><NumberInput step={0.1} value={design.layout?.title_free_y} min={0} max={100} onChange={(v)=>patch("layout.title_free_y", v)} testid="layout-title-free-y"/></Field>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Nudge X (px)"><NumberInput value={design.layout?.title_offset_x} min={-300} max={300} onChange={(v)=>patch("layout.title_offset_x", v)} testid="layout-title-offset-x"/></Field>
                    <Field label="Nudge Y (px)"><NumberInput value={design.layout?.title_offset_y} min={-100} max={100} onChange={(v)=>patch("layout.title_offset_y", v)} testid="layout-title-offset-y"/></Field>
                  </div>
                )}
              </>
            )}

            <p className="text-[10px] text-zinc-400 leading-relaxed mt-2">
              Hide both the business name and the title to give the logo full ownership of the header area.
              Preview and PDFs will reflect these settings independently for Estimates and Invoices.
            </p>
          </Section>

          {/* Logo & branding */}
          <Section title="Logo & branding" defaultOpen>
            <div className="space-y-3">
              {/* Upload / status row */}
              <div className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-md p-3">
                <div className="w-14 h-14 rounded bg-white border border-zinc-200 flex items-center justify-center overflow-hidden shrink-0" data-testid="logo-current-preview">
                  {effectiveLogoUrl
                    ? <img src={effectiveLogoUrl} alt="" className="max-w-full max-h-full object-contain"/>
                    : <ImageIcon className="w-5 h-5 text-zinc-300"/>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">
                    {templateHasCustomLogo
                      ? "Custom logo for this template"
                      : businessLogoUrl
                        ? "Using business logo from Settings"
                        : "No logo set — upload one below or in Settings"}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    {templateHasCustomLogo
                      ? "This template overrides the business logo."
                      : "You can upload a template-specific logo to override the business logo."}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button variant="outline" size="sm" onClick={pickTemplateLogo} data-testid="template-logo-upload-btn">
                    <UploadIcon className="w-3.5 h-3.5 mr-1"/> {templateHasCustomLogo ? "Replace" : "Upload"}
                  </Button>
                  {templateHasCustomLogo && (
                    <Button variant="outline" size="sm" onClick={clearTemplateLogo} data-testid="template-logo-remove-btn">
                      <Trash2 className="w-3.5 h-3.5 mr-1 text-rose-500"/> Remove
                    </Button>
                  )}
                </div>
                <input ref={logoFileInputRef} type="file" accept="image/*" onChange={onLogoFile} className="hidden" data-testid="template-logo-file-input"/>
              </div>

              {/* Positioning mode */}
              <Field label="Positioning mode">
                <SegmentedInput
                  value={design.layout?.logo_layout_mode || "anchored"}
                  onChange={(v)=>patch("layout.logo_layout_mode", v)}
                  options={[["anchored","Anchored (align)"] ,["free","Free (drag anywhere)"]]}
                  testid="layout-logo-mode"
                />
              </Field>

              {/* Anchored controls */}
              {(design.layout?.logo_layout_mode || "anchored") === "anchored" && (
                <>
                  <Field label="Alignment">
                    <SegmentedInput value={design.layout?.logo_position} onChange={(v)=>patch("layout.logo_position", v)} options={[["top-left","Left"],["top-center","Center"],["top-right","Right"],["hidden","Hide"]]} testid="layout-logo-position"/>
                  </Field>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Nudge X (px)"><NumberInput value={design.layout?.logo_offset_x} min={-200} max={200} onChange={(v)=>patch("layout.logo_offset_x", v)} testid="layout-logo-offset-x"/></Field>
                    <Field label="Nudge Y (px)"><NumberInput value={design.layout?.logo_offset_y} min={-100} max={100} onChange={(v)=>patch("layout.logo_offset_y", v)} testid="layout-logo-offset-y"/></Field>
                  </div>
                </>
              )}

              {/* Free-position drag canvas */}
              {design.layout?.logo_layout_mode === "free" && (
                <div>
                  <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-1">Drag to position</div>
                  <LogoDragCanvas
                    logoUrl={effectiveLogoUrl}
                    freeX={design.layout?.logo_free_x ?? 0}
                    freeY={design.layout?.logo_free_y ?? 0}
                    logoHeight={design.layout?.logo_size_px ?? 64}
                    onChange={(partial) => {
                      // Batch multiple layout updates atomically so freeX+freeY don't race.
                      setDesign(prev => {
                        const next = JSON.parse(JSON.stringify(prev || {}));
                        next.layout = next.layout || {};
                        if (partial.freeX !== undefined) next.layout.logo_free_x = Math.round(partial.freeX * 100) / 100;
                        if (partial.freeY !== undefined) next.layout.logo_free_y = Math.round(partial.freeY * 100) / 100;
                        if (partial.logoHeight !== undefined) {
                          next.layout.logo_size_px = partial.logoHeight;
                          next.layout.logo_width_px = null; // let aspect ratio auto
                        }
                        return next;
                      });
                      setDirty(true);
                    }}
                  />
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Field label="X (%)">
                      <NumberInput step={0.1} value={design.layout?.logo_free_x} min={0} max={100} onChange={(v)=>patch("layout.logo_free_x", v)} testid="layout-logo-free-x"/>
                    </Field>
                    <Field label="Y (%)">
                      <NumberInput step={0.1} value={design.layout?.logo_free_y} min={0} max={100} onChange={(v)=>patch("layout.logo_free_y", v)} testid="layout-logo-free-y"/>
                    </Field>
                  </div>
                </div>
              )}

              {/* Shared size + fit + corner controls */}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Logo height (px)"><NumberInput value={design.layout?.logo_size_px} min={24} max={320} onChange={(v)=>patch("layout.logo_size_px", v)} testid="layout-logo-size"/></Field>
                <Field label="Width (px, blank=auto)"><NumberInput value={design.layout?.logo_width_px} min={24} max={600} onChange={(v)=>patch("layout.logo_width_px", v)} testid="layout-logo-width"/></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Logo fit">
                  <SegmentedInput value={design.layout?.logo_object_fit || "contain"} onChange={(v)=>patch("layout.logo_object_fit", v)} options={[["contain","Contain"],["cover","Cover (crop)"]]} testid="layout-logo-fit"/>
                </Field>
                <Field label="Corner radius"><NumberInput value={design.layout?.logo_border_radius_px} min={0} max={80} onChange={(v)=>patch("layout.logo_border_radius_px", v)} testid="layout-logo-radius"/></Field>
              </div>
              <p className="text-[10px] text-zinc-400 leading-relaxed">
                Width left blank preserves the aspect ratio of your uploaded logo.
                Position and size are saved with this template and used in every generated PDF or share link.
              </p>
            </div>
          </Section>

          {/* Layout */}
          <Section title="Layout & placement">
            <Field label="Header style">
              <SegmentedInput value={design.layout?.header_style} onChange={(v)=>patch("layout.header_style", v)} options={[["plain","Plain"],["line","Underline"],["band","Color band"]]} testid="layout-header-style"/>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Meta info align">
                <SegmentedInput value={design.layout?.meta_align} onChange={(v)=>patch("layout.meta_align", v)} options={[["left","L"],["center","C"],["right","R"]]} testid="layout-meta-align"/>
              </Field>
              <Field label="Totals align">
                <SegmentedInput value={design.layout?.totals_align} onChange={(v)=>patch("layout.totals_align", v)} options={[["left","L"],["center","C"],["right","R"]]} testid="layout-totals-align"/>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Page margin (mm)"><NumberInput value={design.layout?.page_margin_mm} min={5} max={40} onChange={(v)=>patch("layout.page_margin_mm", v)} testid="layout-page-margin"/></Field>
              <Field label="Section spacing (px)"><NumberInput value={design.layout?.section_spacing_px} min={8} max={64} onChange={(v)=>patch("layout.section_spacing_px", v)} testid="layout-section-spacing"/></Field>
            </div>
          </Section>

          {/* Table */}
          <Section title="Line-item table">
            <div className="grid grid-cols-2 gap-2">
              <ColorField label="Header bg" value={design.table?.header_bg} onChange={(v)=>patch("table.header_bg", v)} testid="table-header-bg"/>
              <ColorField label="Header text" value={design.table?.header_text} onChange={(v)=>patch("table.header_text", v)} testid="table-header-text"/>
              <ColorField label="Border color" value={design.table?.border_color} onChange={(v)=>patch("table.border_color", v)} testid="table-border-color"/>
              <ColorField label="Stripe bg" value={design.table?.row_stripe_bg} onChange={(v)=>patch("table.row_stripe_bg", v)} testid="table-stripe-bg"/>
            </div>
            <Field label="Border style">
              <SegmentedInput value={design.table?.border_style} onChange={(v)=>patch("table.border_style", v)} options={[["none","None"],["horizontal","Rows"],["full","Full"]]} testid="table-border-style"/>
            </Field>
            <Field label="Cell padding (px)"><NumberInput value={design.table?.cell_padding_px} min={4} max={24} onChange={(v)=>patch("table.cell_padding_px", v)} testid="table-cell-padding"/></Field>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1">
              <Toggle label="Zebra stripes" checked={design.table?.row_stripe} onChange={(v)=>patch("table.row_stripe", v)} testid="table-row-stripe"/>
              <Toggle label="Show qty column" checked={design.table?.show_qty_col} onChange={(v)=>patch("table.show_qty_col", v)} testid="table-show-qty"/>
              <Toggle label="Show unit column" checked={design.table?.show_unit_col} onChange={(v)=>patch("table.show_unit_col", v)} testid="table-show-unit"/>
              <Toggle label="Show tax column" checked={design.table?.show_tax_col} onChange={(v)=>patch("table.show_tax_col", v)} testid="table-show-tax"/>
            </div>
          </Section>

          {/* Sections toggles */}
          <Section title="Sections & fields">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <Toggle label="From (business)" checked={design.sections?.show_business_block} onChange={(v)=>patch("sections.show_business_block", v)} testid="section-show-business"/>
              <Toggle label="Bill to (customer)" checked={design.sections?.show_customer_block} onChange={(v)=>patch("sections.show_customer_block", v)} testid="section-show-customer"/>
              <Toggle label="Notes" checked={design.sections?.show_notes} onChange={(v)=>patch("sections.show_notes", v)} testid="section-show-notes"/>
              <Toggle label="Terms" checked={design.sections?.show_terms} onChange={(v)=>patch("sections.show_terms", v)} testid="section-show-terms"/>
              <Toggle label="Payment instructions" checked={design.sections?.show_payment_instructions} onChange={(v)=>patch("sections.show_payment_instructions", v)} testid="section-show-payment"/>
              <Toggle label="Signature line" checked={design.sections?.show_signature_line} onChange={(v)=>patch("sections.show_signature_line", v)} testid="section-show-signature"/>
              <Toggle label="Photos" checked={design.sections?.show_photos} onChange={(v)=>patch("sections.show_photos", v)} testid="section-show-photos"/>
              <Toggle label="Line item description" checked={design.sections?.show_line_item_description} onChange={(v)=>patch("sections.show_line_item_description", v)} testid="section-show-li-desc"/>
              <Toggle label="Line item add-ons" checked={design.sections?.show_line_item_addons} onChange={(v)=>patch("sections.show_line_item_addons", v)} testid="section-show-li-addons"/>
            </div>
          </Section>

          {/* Content */}
          <Section title="Custom text">
            <Field label="Header line (optional, all caps small)"><Input value={design.content?.header_text || ""} onChange={(e)=>patch("content.header_text", e.target.value)} placeholder="e.g. Confidential — Draft" data-testid="content-header-text"/></Field>
            <Field label="Notes label"><Input value={design.content?.notes_label || ""} onChange={(e)=>patch("content.notes_label", e.target.value)} data-testid="content-notes-label"/></Field>
            <Field label="Terms label"><Input value={design.content?.terms_label || ""} onChange={(e)=>patch("content.terms_label", e.target.value)} data-testid="content-terms-label"/></Field>
            <Field label="Payment instructions"><Textarea rows={3} value={design.content?.payment_instructions || ""} onChange={(e)=>patch("content.payment_instructions", e.target.value)} data-testid="content-payment-instructions"/></Field>
            <Field label="Footer text"><Textarea rows={2} value={design.content?.footer_text || ""} onChange={(e)=>patch("content.footer_text", e.target.value)} data-testid="content-footer-text"/></Field>
            <p className="text-[10px] text-zinc-400 leading-relaxed">
              Tip: Change the visible &ldquo;Estimate&rdquo; and &ldquo;Invoice&rdquo; labels in the <em>Business name and title</em> section above.
            </p>
          </Section>
        </div>

        {/* RIGHT — Live preview */}
        <div className="col-span-12 lg:col-span-6">
          <div className="sticky top-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500">Live preview</span>
              <div className="inline-flex bg-white border border-zinc-200 rounded-md overflow-hidden ml-2" data-testid="preview-kind-toggle">
                <button onClick={()=>setPreviewKind("estimate")} className={`px-3 py-1 text-xs inline-flex items-center gap-1 ${previewKind==="estimate"?"bg-[#FF5A00] text-white":"text-zinc-600"}`} data-testid="preview-kind-estimate"><FileText className="w-3 h-3"/> Estimate</button>
                <button onClick={()=>setPreviewKind("invoice")} className={`px-3 py-1 text-xs inline-flex items-center gap-1 ${previewKind==="invoice"?"bg-emerald-600 text-white":"text-zinc-600"}`} data-testid="preview-kind-invoice"><Receipt className="w-3 h-3"/> Invoice</button>
              </div>
              <div className="flex-1"/>
              {dirty && <span className="text-xs text-amber-600 font-mono-accent uppercase tracking-widest">Unsaved</span>}
              <Button onClick={save} disabled={saving || !dirty} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="template-save-btn">
                <Save className="w-4 h-4 mr-1"/> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
            <div className="border border-zinc-200 rounded-lg overflow-hidden bg-zinc-50 shadow-sm" style={{ height: "80vh" }} data-testid="preview-frame-wrap">
              <iframe ref={iframeRef} title="preview" className="w-full h-full bg-white" data-testid="preview-iframe"/>
            </div>
          </div>
        </div>
      </div>

      <NewTemplateDialog open={newOpen} onOpenChange={setNewOpen} presets={presets} form={newForm} setForm={setNewForm} onCreate={createTemplate} />
    </div>
  );
}

// ---------- Small components ----------

function Header({ onNew }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div>
        <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Brand</div>
        <h1 className="font-heading text-4xl font-bold flex items-center gap-3"><Palette className="w-8 h-8 text-[#FF5A00]"/> Document Designer</h1>
        <p className="text-sm text-zinc-500 mt-1">Customize how estimates and invoices look for your business. Web, print, email, and PDF share the same design.</p>
      </div>
      <Button onClick={onNew} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="new-template-btn">
        <Plus className="w-4 h-4 mr-1"/> New template
      </Button>
    </div>
  );
}

function EmptyState({ presets, onCreate }) {
  return (
    <div className="mt-8">
      <div className="text-zinc-600 mb-4">Pick a starting preset — you can fully customize afterwards.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {presets.map(p => (
          <button
            key={p.key}
            onClick={() => onCreate(p)}
            className="text-left p-5 border border-zinc-200 rounded-xl bg-white hover:border-[#FF5A00] hover:shadow-md transition"
            data-testid={`empty-preset-${p.key}`}
          >
            <div className="text-lg font-heading font-bold">{p.name}</div>
            <div className="text-sm text-zinc-500 mt-1">{p.description}</div>
            <div className="mt-4 text-xs text-[#FF5A00] font-mono-accent uppercase tracking-widest">Start with {p.name} →</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function NewTemplateDialog({ open, onOpenChange, presets, form, setForm, onCreate }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-template-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">New template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Template name"><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} placeholder="e.g. Residential" data-testid="new-template-name"/></Field>
          <Field label="Starting preset">
            <Select value={form.preset} onValueChange={(v)=>setForm({...form, preset: v})}>
              <SelectTrigger data-testid="new-template-preset"><SelectValue/></SelectTrigger>
              <SelectContent>
                {presets.map(p => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button onClick={onCreate} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="new-template-create-btn">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <button onClick={()=>setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 text-left" data-testid={`section-toggle-${title.replace(/\s+/g,'-').toLowerCase()}`}>
        <span className="font-semibold text-sm">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-zinc-400"/> : <ChevronRight className="w-4 h-4 text-zinc-400"/>}
      </button>
      {open && <div className="p-4 pt-2 border-t border-zinc-100 space-y-3">{children}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest font-mono-accent text-zinc-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function NumberInput({ value, onChange, min, max, step = 1, testid }) {
  return (
    <Input
      type="number"
      value={value ?? ""}
      min={min}
      max={max}
      step={step}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "" ? null : (step < 1 ? parseFloat(v) : parseInt(v)));
      }}
      data-testid={testid}
    />
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest font-mono-accent text-zinc-500">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e)=>onChange(e.target.value)}
          className="w-9 h-9 rounded border border-zinc-200 cursor-pointer p-0"
          data-testid={`${testid}-picker`}
        />
        <Input value={value || ""} onChange={(e)=>onChange(e.target.value)} className="font-mono-accent text-xs flex-1" data-testid={`${testid}-input`}/>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
      <input type="checkbox" checked={!!checked} onChange={(e)=>onChange(e.target.checked)} data-testid={testid}/>
      <span>{label}</span>
    </label>
  );
}

function SegmentedInput({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex bg-zinc-100 rounded-md p-0.5 gap-0.5 w-full" data-testid={testid}>
      {options.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`flex-1 px-2 py-1 text-xs rounded transition ${value === v ? "bg-white shadow-sm text-zinc-900 font-medium" : "text-zinc-500 hover:text-zinc-900"}`}
          data-testid={`${testid}-${v}`}
          type="button"
        >{label}</button>
      ))}
    </div>
  );
}

// ---------- Template thumbnail (live-rendered iframe scaled to sidebar width) ----------
function TemplateThumbnail({ template, selected, onSelect }) {
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);

  // Render preview HTML server-side and inject into scaled iframe
  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const res = await api.post("/document-templates/preview", { kind: "estimate", design: template.design }, { responseType: "text", headers: { Accept: "text/html" } });
        if (canceled) return;
        const html = typeof res.data === "string" ? res.data : "";
        const doc = iframeRef.current?.contentDocument;
        if (doc) { doc.open(); doc.write(html); doc.close(); }
        setReady(true);
      } catch { /* ignore */ }
    })();
    return () => { canceled = true; };
  }, [template.id, template.design]);

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left rounded-lg border overflow-hidden transition ${selected ? "border-[#FF5A00] ring-2 ring-orange-200" : "border-zinc-200 hover:border-zinc-400"} bg-white`}
      data-testid={`template-item-${template.id}`}
    >
      {/* Thumbnail preview — scaled iframe, pointer-events disabled */}
      <div className="relative bg-zinc-100 overflow-hidden" style={{ height: 140 }}>
        <iframe
          ref={iframeRef}
          title={`thumb-${template.id}`}
          className="border-0"
          style={{
            width: 850,
            height: 1100,
            transform: "scale(0.19)",
            transformOrigin: "top left",
            pointerEvents: "none",
            backgroundColor: "white",
          }}
          data-testid={`template-thumb-iframe-${template.id}`}
        />
        {!ready && <div className="absolute inset-0 flex items-center justify-center text-[10px] text-zinc-400 font-mono-accent">Rendering…</div>}
        <div className="absolute inset-0 bg-transparent"/>
      </div>
      <div className="p-2 border-t border-zinc-100">
        <div className="text-sm font-medium truncate" title={template.name}>{template.name}</div>
        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
          <span className="text-[9px] font-mono-accent uppercase tracking-widest text-zinc-500">{template.preset}</span>
          {template.is_default_estimate && <span className="text-[9px] px-1 rounded bg-orange-100 text-[#E04F00]">EST</span>}
          {template.is_default_invoice && <span className="text-[9px] px-1 rounded bg-emerald-100 text-emerald-700">INV</span>}
        </div>
      </div>
    </button>
  );
}



/**
 * downscaleImageToDataUrl
 * ------------------------
 * Read an image file, downscale so the longer edge is at most `maxSize` px
 * (preserving aspect ratio), and return a PNG data URL. Keeps the payload
 * small enough to embed inline in the template document so it renders
 * everywhere without a separate CDN fetch.
 */
export async function downscaleImageToDataUrl(file, maxSize = 800) {
  const src = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
  const { naturalWidth: w, naturalHeight: h } = img;
  const longest = Math.max(w, h);
  const scale = longest > maxSize ? maxSize / longest : 1;
  const outW = Math.max(1, Math.round(w * scale));
  const outH = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, outW, outH);
  return canvas.toDataURL("image/png");
}
